import React, { useCallback, useContext, useMemo } from "react";

import { useLabels } from "api/services/useLabels";
import { UNSURE_LABEL } from "config/LabelMapping";
import { BoundingBoxesContext, useBoundingBoxesContext } from "context/boundingBoxesContext";
import { getFrontendLabelValueFromBackendLabel } from "features/queryDetailsModal/utils";
import { useAppDispatch } from "hooks/store";
import { setUnsavedAnnotations } from "store/slices/FocusModal/focusModalSlice";
import { DetectorModes } from "types/createDetector";
import { ModeConfiguration } from "types/detectors";
import { CanvasAnnotation } from "types/imageAnnotations";
import { DisplayBBoxInfo, REVIEW_REASON_CHOICES } from "types/imageQueries";
import { LabelValue } from "types/labelValue";
import { createArrayRois } from "utils/ReviewPageUtils";

import { RelabelActionsView } from "./RelabelActionsView";

export interface RelabelSectionQueryDetailsProps {
  onClose?: () => void;
  onSubmit: () => Promise<void>;
  posicheckId?: string;
  selectedLabelValue?: LabelValue | null;
  mode: DetectorModes;
  modeConfig?: ModeConfiguration;
  reviewBBoxInfo?: DisplayBBoxInfo;
}

export function RelabelSectionQueryDetails(props: RelabelSectionQueryDetailsProps) {
  const { onClose, posicheckId, selectedLabelValue, onSubmit, mode, modeConfig, reviewBBoxInfo } = props;

  const contextValue = useContext(BoundingBoxesContext);
  const { createLabelAsync, isCreatingLabel } = useLabels();
  const dispatch = useAppDispatch();

  const handlePostSubmit = useCallback(() => {
    onClose?.();
    onSubmit();
  }, [onClose, onSubmit]);

  const sendLabel = useCallback(
    async (label: string, feedbackText?: string) => {
      try {
        if (posicheckId) {
          let rois: CanvasAnnotation[] | undefined;
          if (contextValue && contextValue.isBoundingBoxes) {
            const { getValidBoxes } = contextValue;
            rois = createArrayRois(getValidBoxes());
          }

          await createLabelAsync({
            posicheck_id: posicheckId,
            label,
            review_reason: REVIEW_REASON_CHOICES.CUSTOMER_INITIATED,
            rois,
            ...(feedbackText && {
              feedback_text: feedbackText,
            }),
            selected_label_value: selectedLabelValue,
            review_bbox_info: reviewBBoxInfo,
          });

          if (rois && rois.length > 0) {
            dispatch(setUnsavedAnnotations(false));
          }

          handlePostSubmit();
        }
      } catch (error) {
        console.error("Error sending label:", error);
      }
    },
    [contextValue, dispatch, handlePostSubmit, posicheckId, createLabelAsync, selectedLabelValue, reviewBBoxInfo]
  );

  const handleLabelSubmit = useCallback(
    (label: string | number) => {
      sendLabel(label.toString());
    },
    [sendLabel]
  );

  const handleUnclearSubmit = useCallback(
    (feedbackText: string) => {
      sendLabel(UNSURE_LABEL, feedbackText);
    },
    [sendLabel]
  );

  const { annotations, validBoxLabels } = useBoundingBoxesContext();
  const closeButtonTopPosition = useMemo(
    () => (mode === DetectorModes.BINARY && validBoxLabels.length > 0 ? "39px" : "9px"),
    [mode, validBoxLabels.length]
  );

  return (
    <RelabelActionsView
      containerId="relabel-section-query-details"
      isLoading={isCreatingLabel || !posicheckId}
      onClose={onClose || (() => {})}
      posicheckId={posicheckId}
      lastLabel={selectedLabelValue ? getFrontendLabelValueFromBackendLabel(selectedLabelValue, mode) : null}
      mode={mode}
      modeConfig={modeConfig}
      onLabelSubmit={handleLabelSubmit}
      onUnclearSubmit={handleUnclearSubmit}
      rois={annotations}
      closeButtonTopPosition={closeButtonTopPosition}
    />
  );
}
